import { Cancel } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { StyledButton } from "../../../../components/Button/Button";
import { AddonLabel, Card, LogoWrapper } from "../../Quotes.style";
import { FilterNames, FilterValue } from "../Filters/Filters.style";
import {
  PremiumInterface,
  ProductInterface,
  useGetQuotes,
} from "./../../../../services/QuoteServices";
import { commaSeparatedPrice } from "./../../../../utils";
import { Skeleton, CircularProgress, Box } from "@mui/material";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useInitiateProposalMutation } from "../../../../services/ProposalServices";
import useUrlQuery from "../../../../hooks/useUrlQuery";
import { useDispatch, useSelector } from "react-redux";
import {
  addOnsSlugSelector,
  editExistingPolicyDetails,
  openSeeDetails,
} from "../../../../modules/quotes.slice";
import TextButton from "../../../../components/TextButton/TextButton";
import PADisclaimer from "../PADisclaimer/PADisclaimer";

interface QuoteCardProps {
  quoteData: {
    isQuoteLoading: boolean;
    premium_data: PremiumInterface | undefined;
    product_data: ProductInterface | undefined;
  };
}

interface OptedAddOn {
  slug: string;
  premium: string;
  name: string;
}

const AddOn: React.FC<{
  isChecked: boolean;
  name: string;
  premium: string;
}> = ({ name, isChecked, premium }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: { xs: "14px", sm: "16px" },
    }}
  >
    {isChecked ? (
      <>
        {name} - {premium}
      </>
    ) : (
      <>
        <Cancel color="error" fontSize="small" /> {name}
      </>
    )}
  </Box>
);

const QuoteCard: React.FC<QuoteCardProps> = ({ quoteData }) => {
  const { isQuoteLoading, premium_data, product_data } = quoteData;
  const { getUrlQuery } = useUrlQuery();
  const dispatch = useDispatch();
  const stage = getUrlQuery("stage");
  const { isPADisclaimerAccepted, selectedAddons } = useTypedSelector(
    (state) => state.quote
  );

  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const addons_name_slug = useSelector(
    addOnsSlugSelector
  ) as { [key: string]: string };
  const [opted, setOpted] = useState<OptedAddOn[]>([]);

  const { initiateProposal, isSubmitting } = useInitiateProposalMutation();

  const canOpt: { [key: string]: string } =
    premium_data?.PREMIUM_DATA?.ADDONS?.CAN_OPT || {};
  const included: { [key: string]: string } =
    premium_data?.PREMIUM_DATA?.ADDONS?.INCLUDED || {};

  // prepare all add-ons list for display
  const all_add_ons = [
    ...Object.keys(canOpt).filter(
      (item) =>
        selectedAddons.includes(item) &&
        canOpt[item] !== "NOT FOUND" &&
        canOpt[item] !== "Not Found"
    ),
    ...Object.keys(included).filter((item) => included[item] !== "NOT FOUND"),
  ];

  // set opted add-ons whenever selectedAddons or canOpt changes
  useEffect(() => {
    if (!canOpt) return;
    const added: OptedAddOn[] = [];

    Object.entries(canOpt).forEach(([item, value]) => {
      if (selectedAddons.includes(item)) {
        added.push({
          slug: item,
          premium: value ?? "0",
          name: addons_name_slug?.[item] ?? item,
        });
      }
    });

    setOpted(added);
  }, [canOpt, selectedAddons, addons_name_slug]);

  const additionalPremium = getAddOnPremium(opted);
  const isOD = product_data?.POLICY_TYPE === "OD";

  const onBuyNow = () => {
    const hasPACover = selectedAddons.includes("PA_COVER");

    if (stage !== "PF") {
      dispatch(editExistingPolicyDetails());
      return;
    }

    if (hasPACover) {
      setOpen(false);
    } else if (!(isOD || isPADisclaimerAccepted)) {
      setOpen(true);
      return;
    }

    if (product_data && premium_data) {
      initiateProposal({
        PRODUCT_ID: product_data.PRODUCTID,
        COMPANY_SLUG: product_data.INSURANCE_SLUG,
        TOTAL_PREMIUM: (
          +premium_data?.PREMIUM_DATA.TOTAL_PREMIUM + additionalPremium
        ).toString(),
        GST:
          +premium_data?.PREMIUM_DATA.TOTAL_PREMIUM -
          +premium_data?.PREMIUM_DATA.NET_PREMIUM,
        IDV: premium_data.PREMIUM_DATA.IDV,
        BASIC_OD: premium_data.PREMIUM_DATA.BASIC_OD,
        BASIC_TP: premium_data.PREMIUM_DATA.BASIC_TP,
        NCB_DISCOUNT: premium_data.PREMIUM_DATA.NCB_DISCOUNT,
        ADDONS: opted,
        CORRELATION_ID: premium_data?.PREMIUM_DATA?.CORRELATION_ID,
      });
    }
  };

  return (
    <>
      <Card>
        {!isQuoteLoading && (
          <div className="plan-type">{product_data?.PRODUCT_NAME}</div>
        )}
        <div className="quote-flex-between">
          <LogoWrapper>
            {isQuoteLoading ? (
              <Skeleton variant="rectangular" height="60px" />
            ) : (
              <img src={product_data?.LOGO_PATH} alt="logo" />
            )}
          </LogoWrapper>
          <Box display="flex" alignItems="center" gap={2}>
            <FilterNames>IDV:</FilterNames>
            {isQuoteLoading ? (
              <Skeleton variant="text" width="100px" />
            ) : (
              <FilterValue>
                {commaSeparatedPrice(premium_data?.PREMIUM_DATA.IDV)}
              </FilterValue>
            )}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <StyledButton
              isBuyNow
              variant="outlined"
              enlarged
              onClick={onBuyNow}
              sx={{
                padding: "10px 0",
                width: "174px",
                justifyContent: "space-evenly",
                border: "1px solid var(--secondary-color)",
                backgroundColor: "#fff",
                marginBottom: "8px",
                "& .btn-span": {
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--tertiary-color)",
                  marginRight: "8px",
                },
                "& .price": {
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "var(--secondary-color)",
                },
                "&:hover": {
                  backgroundColor: "var(--secondary-color)",
                  "& .btn-span, & .price": {
                    color: "#fff",
                  },
                },
              }}
            >
              {isQuoteLoading ? (
                <CircularProgress sx={{ color: "var(--secondary-color)" }} />
              ) : (
                <>
                  <span className="btn-span">Buy Now</span>
                  <span className="price">
                    {commaSeparatedPrice(
                      premium_data?.PREMIUM_DATA.TOTAL_PREMIUM,
                      additionalPremium
                    )}
                  </span>
                </>
              )}
            </StyledButton>
            <TextButton
              onClick={() => {
                const totalPremiumWithAddons =
                  (Number(premium_data?.PREMIUM_DATA.TOTAL_PREMIUM) || 0) +
                  (isNaN(additionalPremium) ? 0 : additionalPremium);
                dispatch(
                  openSeeDetails({
                    premium_data,
                    product_data,
                    opted,
                    totalPremiumWithAddons,
                  })
                );
              }}
              color="primary"
              className="quote-plan-details-btn"
            >
              View Plan Details
            </TextButton>
          </Box>
        </div>

        {all_add_ons.length > 0 && (
          <div className="quote-addons-list">
            {all_add_ons.map((item) => (
              <AddonLabel key={item}>
                <div className="name">{addons_name_slug?.[item] ?? item}:</div>
                <div className="premium">
                  ₹{canOpt?.[item] || included?.[item] || "0"}
                </div>
              </AddonLabel>
            ))}
          </div>
        )}
      </Card>
      <PADisclaimer onBuyNow={onBuyNow} open={open} onClose={onClose} />
    </>
  );
};

export default QuoteCard;

function getAddOnPremium(opted: OptedAddOn[]): number {
  return opted.reduce((sum, addon) => {
    const num = Number(addon.premium);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);
}
